import React from 'react';
import AIGenerationProgress from '@/components/loading/AIGenerationProgress';
import { FadeIn } from '@/components/transitions/FadeTransition';

function NotificationPanel({
  successMessages,
  setSuccessMessages,
  noteError,
  error,
  setError,
  fetchNoteData,
  soapProgress
}) {
  return (
    <>
      {soapProgress && (
        <FadeIn show={true} duration={300}>
        <div className="px-4 mb-4">
          <div className="px-4 py-3 bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700 rounded-2xl text-blue-800 dark:text-blue-200">
            {(soapProgress.status === 'generating' || soapProgress.status === 'processing' || soapProgress.status === 'saving' || soapProgress.status === 'preamble') ? (
              <AIGenerationProgress
                message="Writing your note..."
                currentStep={
                  soapProgress.status === 'generating' ? 'reading'
                  : soapProgress.status === 'processing' ? 'drafting'
                  : 'formatting'
                }
                autoAdvance={false}
              />
            ) : soapProgress.status === 'complete' ? (
              <div className="flex items-center">
                <div className="flex-shrink-0 mr-3">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-medium">{soapProgress.message}</p>
                  {soapProgress.note_data && (
                    <button
                      className="mt-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      onClick={() => fetchNoteData(soapProgress.note_data.note_id)}
                    >
                      [Review Note]
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="font-medium">{soapProgress.message}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </FadeIn>
      )}

      {successMessages.length > 0 && (
        <FadeIn show={true} duration={300}>
        <div className="px-4 space-y-2 mb-4">
          {successMessages.map((message, index) => (
            <div
              key={message.noteId}
              className="px-4 py-3 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-2xl text-green-800 dark:text-green-200 flex items-center justify-between"
            >
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>
                  ✅ {message.noteType} note saved for {message.clientName} on {message.date}.{' '}
                  <button
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    onClick={() => {
                      fetchNoteData(message.noteId);
                    }}
                  >
                    [View Note]
                  </button>
                </span>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => {
                  setSuccessMessages(prevMessages =>
                    prevMessages.filter(msg => msg.noteId !== message.noteId)
                  );
                }}
                aria-label="Dismiss notification"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          ))}
        </div>
        </FadeIn>
      )}

      {noteError && (
        <FadeIn show={true} duration={300}>
        <div className="px-4 mb-4 p-4 border border-red-300 dark:border-red-700 rounded-2xl bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-200">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{noteError.message}</span>
          </div>
        </div>
        </FadeIn>
      )}
      
      {error && (
        <FadeIn show={true} duration={300}>
        <div className="px-4 mb-4 p-4 border border-red-300 dark:border-red-700 rounded-2xl bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Dismiss error"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        </FadeIn>
      )}
    </>
  );
}

export default NotificationPanel;
